import React, { memo, useMemo } from 'react';
import { View, ActivityIndicator, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { MarketplaceListing } from '@/types/residents/marketplaceListing';
import { MarketplaceListingCard } from './MarketplaceListingCard';
import { SectionHeader } from '../SectionHeader';

const DEFAULT_COLUMNS = 2;
const CARD_HEIGHT_MOBILE = 246;
const CARD_HEIGHT_DESKTOP = 300;

interface MarketplaceListingsGridProps {
  listings: MarketplaceListing[];
  onListingPress: (listing: MarketplaceListing) => void;
  onViewAll?: () => void;
  isLoading?: boolean;
  itemStyle?: StyleProp<ViewStyle>;
  numColumns?: number;
}

export const MarketplaceListingsGrid: React.FC<MarketplaceListingsGridProps> = memo(({
  listings,
  onListingPress,
  onViewAll,
  isLoading = false,
  itemStyle,
  numColumns = DEFAULT_COLUMNS,
}) => {
  const { colors } = useThemeColors();
  const dynamicItemStyle = useMemo<ViewStyle>(() => {
    const basis = `${Math.floor(100 / numColumns) - 2}%`;
    return {
      flexBasis: basis as any,
      flexGrow: 0,
      flexShrink: 0,
      minHeight: numColumns >= 3 ? CARD_HEIGHT_DESKTOP : CARD_HEIGHT_MOBILE,
    };
  }, [numColumns]);

  return (
    <View>
      <SectionHeader
        title="Listings"
        actionLabel={onViewAll ? 'View all' : undefined}
        onAction={onViewAll}
      />

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={PRIMITIVE_COLORS.resioBlue} />
        </View>
      ) : listings.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={[styles.emptyTitle, { color: colors.text.secondary }]}>No listings found</Text>
          <Text style={[styles.emptySubtitle, { color: colors.text.dim }]}>Try adjusting your filters or search</Text>
        </View>
      ) : (
        <View style={styles.grid}>
          {listings.map((listing) => (
            <MarketplaceListingCard
              key={listing.id}
              listing={listing}
              onPress={() => onListingPress(listing)}
              style={[dynamicItemStyle, itemStyle]}
            />
          ))}
        </View>
      )}
    </View>
  );
});

MarketplaceListingsGrid.displayName = 'MarketplaceListingsGrid';

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: REDESIGN_SPACING.gridGap,
  },
  loadingContainer: {
    paddingVertical: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyContainer: {
    paddingVertical: 40,
    alignItems: 'center',
    gap: 4,
  },
  emptyTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
  },
  emptySubtitle: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
});
